import { useApolloClient, DocumentNode } from '@apollo/client';
import { saveAs } from 'file-saver';
import { ReactNode, useCallback, useState } from 'react';
import { useFlutter } from './FlutterProvider';
import { useLoadingController } from './LoadingProvider';

export type DownloadFn<Variables> = (props: Variables, preferredFileName?: string) => Promise<void>;

export type DownloadProviderProps<Query, Variables> = {
    children: (fn: DownloadFn<Variables>) => JSX.Element | ReactNode;
    getSignedUrl: (response: Query) => string | null;
    query: DocumentNode;
};

const DownloadProvider = <Query, Variables>({
    children,
    query,
    getSignedUrl,
}: DownloadProviderProps<Query, Variables>) => {
    const [loading, setLoading] = useState(false);
    const { deviceFingerPrint, sendDownloadFile } = useFlutter();

    // get the apollo client
    const client = useApolloClient();

    // use the loading controller
    useLoadingController(loading);

    const saveFileAs = useCallback(async (signedUrl: string, preferredFileName?: string) => {
        if (preferredFileName) {
            const file = await fetch(signedUrl).then(res => res.blob());
            saveAs(file, preferredFileName);

            return;
        }

        saveAs(signedUrl);
    }, []);

    const fn = useCallback(
        async (variables: Variables, preferredFileName?: string) => {
            try {
                // initialize loading
                setLoading(true);

                const response = await client.query<Query, Variables>({
                    query,
                    variables,
                    fetchPolicy: 'no-cache',
                });

                const signedUrl = getSignedUrl(response.data);

                if (signedUrl) {
                    if (deviceFingerPrint) {
                        await sendDownloadFile({ signedUrl, filename: '' });
                    } else {
                        saveFileAs(signedUrl, preferredFileName);
                    }
                }
            } finally {
                // reset
                setLoading(false);
            }
        },
        [client, query, getSignedUrl, deviceFingerPrint, sendDownloadFile, saveFileAs]
    );

    return <>{children(fn)}</>;
};

export default DownloadProvider;
